/** Les montants circulent en centimes. On ne formate qu'a l'affichage. */
export const centimes = (majeur: number): number => Math.round(majeur * 100);

/*
 * LES FORMATS SUIVENT LE PAYS DE L'AGENCE.
 *
 * Une agence de Sydney lit « $1,234.00 » et « 24/10/2026 », une agence de
 * Chicago « 10/24/2026 », une agence d'Alger « 1 234,00 DZD » et « 14h30 ».
 * La configuration est posee une fois par i18n/index.ts — les centaines
 * d'appels a montant() et dateFr() n'ont pas a changer.
 *
 * En francais, le rendu reste EXACTEMENT celui d'avant : les documents, les
 * captures du guide et les controles automatiques en dependent.
 */
interface ConfigFormats {
  locale: string; langue: 'fr' | 'en'; devise: string;
  statuts: Record<string, string>; roles: Record<string, string>;
}
let cfg: ConfigFormats = { locale: 'fr-DZ', langue: 'fr', devise: 'DZD', statuts: {}, roles: {} };

export function configurerFormats(c: ConfigFormats): void {
  cfg = c;
  // Les tables de libelles sont des objets partages : on les remplit sur place,
  // pour que chaque `STATUT_LABEL[x]` du logiciel lise la langue active.
  for (const k of Object.keys(STATUT_LABEL)) delete STATUT_LABEL[k];
  Object.assign(STATUT_LABEL, c.statuts);
  for (const k of Object.keys(ROLE_LABEL)) delete ROLE_LABEL[k];
  Object.assign(ROLE_LABEL, c.roles);
  const en = c.langue === 'en';
  remplir(COUVERTURE_LABEL, en ? COUVERTURE_EN : COUVERTURE_FR);
  remplir(SOURCE_KM_LABEL, en ? SOURCE_KM_EN : SOURCE_KM_FR);
  remplir(LIBELLE_MOUVEMENT, en ? MOUVEMENT_EN : MOUVEMENT_FR);
  CARBURANT_LABEL.splice(0, CARBURANT_LABEL.length, ...(en ? CARBURANT_EN : CARBURANT_FR));
  const jours = Array.from({ length: 7 }, (_, i) =>
    new Date(2024, 0, 7 + i).toLocaleDateString(c.locale, { weekday: 'short' }).replace('.', '').toLowerCase());
  JOURS.splice(0, 7, ...(c.langue === 'fr' ? ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam'] : jours));
}

/** La devise de reference de l'agence, pour qui en a besoin hors d'un montant. */
export const deviseReference = (): string => cfg.devise;

export function montant(minor: number | undefined | null, devise = cfg.devise, decimales = 2): string {
  const v = minor ?? 0;
  if (cfg.langue === 'fr') {
    const neg = v < 0;
    const s = Math.abs(v).toString().padStart(decimales + 1, '0');
    const ent = s.slice(0, s.length - decimales) || '0';
    const dec = s.slice(s.length - decimales);
    return `${neg ? '−' : ''}${ent.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')},${dec} ${devise}`;
  }
  try {
    // « symbol » et non « narrowSymbol » : dans une agence australienne, le
    // tiroir en dollars americains doit se distinguer du dollar local.
    return new Intl.NumberFormat(cfg.locale, {
      style: 'currency', currency: devise, currencyDisplay: 'symbol',
      minimumFractionDigits: decimales, maximumFractionDigits: decimales,
    }).format(v / 100);
  } catch {
    // Une devise inconnue d'Intl ne doit jamais casser un ecran.
    return `${(v / 100).toLocaleString(cfg.locale, { minimumFractionDigits: decimales, maximumFractionDigits: decimales })} ${devise}`;
  }
}

/** Version compacte pour les graphiques et les cellules serrees. */
export function montantCourt(minor: number | undefined | null, devise = cfg.devise): string {
  const v = Math.round((minor ?? 0) / 100);
  if (cfg.langue === 'fr') {
    if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1).replace('.', ',')} M ${devise}`;
    if (Math.abs(v) >= 10_000) return `${Math.round(v / 1000)} k ${devise}`;
    return `${v.toLocaleString('fr-FR').replace(/ /g, ' ')} ${devise}`;
  }
  try {
    return new Intl.NumberFormat(cfg.locale, {
      style: 'currency', currency: devise, currencyDisplay: 'symbol',
      notation: Math.abs(v) >= 10_000 ? 'compact' : 'standard', maximumFractionDigits: 1,
    }).format(v);
  } catch { return `${v.toLocaleString(cfg.locale)} ${devise}`; }
}

const d2 = (n: number) => String(n).padStart(2, '0');
export const dateFr = (d: string | Date | undefined | null): string => {
  if (!d) return '—';
  const x = new Date(d);
  if (cfg.langue === 'fr') return `${d2(x.getDate())}/${d2(x.getMonth() + 1)}/${x.getFullYear()}`;
  return x.toLocaleDateString(cfg.locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
};
export const heureFr = (d: string | Date | undefined | null): string => {
  if (!d) return '—';
  const x = new Date(d);
  if (cfg.langue === 'fr') return `${d2(x.getHours())}h${d2(x.getMinutes())}`;
  return x.toLocaleTimeString(cfg.locale, { hour: '2-digit', minute: '2-digit' });
};
export const dateHeureFr = (d: string | Date | undefined | null): string =>
  d ? `${dateFr(d)} · ${heureFr(d)}` : '—';
export const plageFr = (a: string | Date, b: string | Date): string => {
  const x = new Date(a);
  const debut = cfg.langue === 'fr'
    ? `${d2(x.getDate())}/${d2(x.getMonth() + 1)}`
    : x.toLocaleDateString(cfg.locale, { day: '2-digit', month: '2-digit' });
  return `${debut} → ${dateFr(b)}`;
};
/** Le nom du mois et l'annee, dans la langue de l'ecran : « octobre 2026 », « October 2026 ». */
export const moisLong = (d: Date): string => d.toLocaleDateString(cfg.locale, { month: 'long', year: 'numeric' });
export const isoJour = (d: Date): string => `${d.getFullYear()}-${d2(d.getMonth() + 1)}-${d2(d.getDate())}`;
export const ajouteJours = (d: Date, n: number): Date => new Date(d.getTime() + n * 86400000);

/** Abreviations des jours, dimanche en tete (getDay). Remplies selon la langue active. */
export const JOURS: string[] = ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam'];

/** Le statut technique ne s'affiche jamais : libelles remplis selon la langue active. */
export const STATUT_LABEL: Record<string, string> = {};

/** Le role vit en base sous forme de cle technique ; l'ecran ne montre jamais la cle. */
export const ROLE_LABEL: Record<string, string> = {};
export const roleLabel = (r: string): string => ROLE_LABEL[r] ?? r.replace(/_/g, ' ');

function remplir(cible: Record<string, string>, source: Record<string, string>): void {
  for (const k of Object.keys(cible)) delete cible[k];
  Object.assign(cible, source);
}

const COUVERTURE_FR: Record<string, string> = {
  tous_risques: 'Tous risques', tiers_plus: 'Tiers plus', tiers: 'Au tiers',
};
const COUVERTURE_EN: Record<string, string> = {
  tous_risques: 'Comprehensive', tiers_plus: 'Third party plus', tiers: 'Third party',
};
export const COUVERTURE_LABEL: Record<string, string> = { ...COUVERTURE_FR };

const SOURCE_KM_FR: Record<string, string> = {
  saisie: 'Saisie directe', etat_des_lieux: 'État des lieux', entretien: 'Entretien',
};
const SOURCE_KM_EN: Record<string, string> = {
  saisie: 'Direct entry', etat_des_lieux: 'Inspection', entretien: 'Maintenance',
};
export const SOURCE_KM_LABEL: Record<string, string> = { ...SOURCE_KM_FR };

const CARBURANT_FR = ['vide', '1/4', '1/2', '3/4', 'P'];
const CARBURANT_EN = ['empty', '1/4', '1/2', '3/4', 'F'];
export const CARBURANT_LABEL = [...CARBURANT_FR];

export const nomClient = (c: { nom?: string; prenom?: string; raisonSociale?: string } | null | undefined): string =>
  !c ? '—' : c.raisonSociale || [c.prenom, c.nom].filter(Boolean).join(' ') || '—';

/**
 * Le nom francais d'un mouvement de caisse.
 *
 * 'caution_recue' est la cle stockee : elle ne bouge jamais, la base et le
 * journal en dependent. Les ecrans affichaient `type.replace('_',' ')`, ce qui
 * donnait « caution recue » et « change entree » — des identifiants de code
 * montres a des gens qui n'en sont pas.
 */
const MOUVEMENT_FR: Record<string, string> = {
  encaissement: 'Encaissement',
  acompte: 'Acompte',
  caution_recue: 'Caution reçue',
  caution_restituee: 'Caution restituée',
  depense: 'Dépense',
  apport: 'Apport',
  retrait: 'Retrait',
  change_entree: 'Change — entrée',
  change_sortie: 'Change — sortie',
  transfert_entree: 'Transfert — entrée',
  transfert_sortie: 'Transfert — sortie',
  frais_bancaires: 'Frais bancaires',
};
const MOUVEMENT_EN: Record<string, string> = {
  encaissement: 'Payment',
  acompte: 'Down payment',
  caution_recue: 'Deposit received',
  caution_restituee: 'Deposit refunded',
  depense: 'Expense',
  apport: 'Cash in',
  retrait: 'Withdrawal',
  change_entree: 'Exchange — in',
  change_sortie: 'Exchange — out',
  transfert_entree: 'Transfer — in',
  transfert_sortie: 'Transfer — out',
  frais_bancaires: 'Bank fees',
};
export const LIBELLE_MOUVEMENT: Record<string, string> = { ...MOUVEMENT_FR };
export const libelleMouvement = (t: string) => LIBELLE_MOUVEMENT[t] ?? t.replace(/_/g, ' ');
